import React from 'react';
import { Trophy, Star, TrendingUp } from 'lucide-react';

const TopPerformers = ({ topPerformers = {} }) => {
  // Helper function to format currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '$0.00';
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
    if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
    return `$${numAmount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Handle both array and object formats
  let performers = [];
  if (Array.isArray(topPerformers)) {
    performers = topPerformers;
  } else if (topPerformers && typeof topPerformers === 'object') {
    // Combine top_vendors and top_services
    const topVendors = Array.isArray(topPerformers.top_vendors) ? topPerformers.top_vendors : [];
    const topServices = Array.isArray(topPerformers.top_services) ? topPerformers.top_services : [];
    
    // Map vendors to have business_name
    const vendors = topVendors.map(v => ({
      business_name: v.business_name,
      booking_count: v.booking_count,
      revenue: v.revenue,
      type: 'vendor'
    }));
    
    // Map services to have service_name
    const services = topServices.map(s => ({
      service_name: s.service_name,
      booking_count: s.booking_count,
      revenue: s.revenue,
      type: 'service'
    }));
    
    // Combine and sort by revenue
    performers = [...vendors, ...services]
      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
      .slice(0, 5);
  }

  if (!performers || performers.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Performers</h3>
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No performance data available</p>
        </div>
      </div>
    );
  }

  const getRankIcon = (index) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-500" />;
    if (index === 1) return <Trophy className="w-5 h-5 text-gray-400" />;
    if (index === 2) return <Trophy className="w-5 h-5 text-orange-500" />;
    return <span className="w-5 h-5 flex items-center justify-center text-sm font-bold text-gray-500">{index + 1}</span>;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Top Performers</h3>
        <TrendingUp className="w-5 h-5 text-yellow-600" />
      </div>
      <div className="space-y-3">
        {performers.map((performer, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1 min-w-0">
              {getRankIcon(index)}
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900 truncate">
                  {performer.service_name || performer.business_name || `Vendor ${index + 1}`}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-gray-500">
                    {performer.booking_count || 0} bookings
                  </span>
                  {performer.average_rating && (
                    <div className="flex items-center gap-1">
                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                      <span className="text-xs text-gray-500">
                        {Number(performer.average_rating).toFixed(1)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                {formatCurrency(performer.revenue || performer.total_revenue || 0)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopPerformers;

